import React, { useState, useEffect, useCallback, useRef } from 'react';
import { IcExternalLink, IcPlus, IcCheckCircle, IcAlertCircle, IcImage, IcCamera, IcClose } from '../../theme/icons';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import customerService from '../../services/customerService';
import clientRecordService from '../../services/clientRecordService';
import ServiceRecordDetailModal, { toUrl } from './ServiceRecordDetailModal';
import { BtnSk, Sk } from '../../components/Skeleton';

const fmtMoney = (a) =>
  `${new Intl.NumberFormat('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(Number(a) || 0)} DT`;
const fmtDate = (d) => { try { return format(new Date(d), 'PPP', { locale: fr }); } catch (_) { return d || '-'; } };
// Date courte pour le rendu compact mobile.
const fmtDateShort = (d) => { try { return format(new Date(d), 'dd/MM/yy'); } catch (_) { return d || '-'; } };

// Each tab shows only its own service(s). Rows come from real ventes — no manual
// "Ajouter". Two modes:
//   mode 'link'  (Soin, Diagnostic visage)  -> open a link / add link or scan QR
//   mode 'photo' (Poids, Diagnostic cheveux) -> add a photo, shown on the page
// compact : rendu mobile — liste empilée (pas de tableau, pas de défilement
// horizontal, dates courtes, boutons icône) — Ahmed 2026-08-16.
const ClientRecordsPanel = ({ customerId, match, category, label, mode = 'link', compact = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // link mode popup
  const [uploadingId, setUploadingId] = useState(null);
  const fileRef = useRef(null);
  const pendingRow = useRef(null);

  // Old diagnostics not tied to a sale (Diagnostic visage only).
  const isDiagnostic = category === 'DIAGNOSTIC';
  const [extras, setExtras] = useState([]);
  const [showOld, setShowOld] = useState(false);
  const [oldForm, setOldForm] = useState({ date: '', link: '', note: '' });
  const [savingOld, setSavingOld] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await customerService.getPurchasedServices(customerId);
      setRows(data);
      // Standalone (off-sale) diagnostics live in the records table with no sale_service.
      if (isDiagnostic) {
        try {
          const recs = await clientRecordService.getRecords({ customer: customerId, category });
          setExtras((recs || []).filter((r) => !r.sale_service));
        } catch (_) { /* non-blocking */ }
      }
    } catch (_) {
      setError('Erreur lors du chargement des services.');
    } finally {
      setLoading(false);
    }
  }, [customerId, isDiagnostic, category]);

  useEffect(() => { load(); }, [load]);

  const matchRow = (r) => {
    const name = (r.service_name || '').toLowerCase();
    if (match?.name) return name.includes(match.name);
    if (match?.type) return r.service_type === match.type;
    return false;
  };
  const shown = rows.filter(matchRow);

  // ---- link mode ----
  const handleLink = (r) => {
    const link = r.record?.link;
    if (link) {
      const openIt = window.confirm(`Lien enregistré :\n${link}\n\nOK = ouvrir   ·   Annuler = modifier`);
      if (openIt) { window.open(toUrl(link), '_blank', 'noopener'); return; }
    }
    setSelected(r);
  };

  // ---- photo mode ----
  const pickPhoto = (r) => { pendingRow.current = r; fileRef.current?.click(); };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const r = pendingRow.current;
    if (!file || !r) return;
    try {
      setUploadingId(r.sale_service_id);
      const fd = new FormData();
      fd.append('customer', customerId);
      fd.append('sale_service', r.sale_service_id);
      fd.append('category', category);
      fd.append('type', r.service_name || '');
      fd.append('image', file);
      await clientRecordService.saveRecord(fd, r.record?.id);
      await load();
    } catch (_) {
      alert("Échec de l'envoi de la photo.");
    } finally {
      setUploadingId(null);
      pendingRow.current = null;
    }
  };

  // ---- old (off-sale) diagnostic ----
  const openOld = () => {
    const today = new Date();
    const d = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    setOldForm({ date: d, link: '', note: '' });
    setShowOld(true);
  };
  const saveOld = async () => {
    if (!oldForm.date) { alert('Veuillez choisir une date.'); return; }
    if (!oldForm.link.trim() && !oldForm.note.trim()) { alert('Ajoutez un lien / QR ou une note.'); return; }
    try {
      setSavingOld(true);
      const fd = new FormData();
      fd.append('customer', customerId);
      fd.append('category', category);
      fd.append('type', label || 'Diagnostic visage');
      fd.append('date', oldForm.date);
      if (oldForm.link.trim()) fd.append('link', oldForm.link.trim());
      if (oldForm.note.trim()) fd.append('note', oldForm.note.trim());
      await clientRecordService.createRecord(fd);
      setShowOld(false);
      await load();
    } catch (_) {
      alert("Échec de l'enregistrement du diagnostic.");
    } finally {
      setSavingOld(false);
    }
  };

  const deleteOld = async (id) => {
    if (!window.confirm('Supprimer cet ancien diagnostic ?')) return;
    try { await clientRecordService.deleteRecord(id); await load(); } catch (_) { alert('Échec de la suppression.'); }
  };

  // Header action: "Ajouter un ancien diagnostic" (Diagnostic visage only).
  const oldControls = isDiagnostic && (
    <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-5 py-3 border-b border-gray-100 bg-slate-50/50">
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Anciens diagnostics{extras.length > 0 ? ` (${extras.length})` : ''}</span>
      {/* Studio 2026-09-08 : mini « + » (Boutons-2 §6.5), icône seule, libellé
          retiré → gardé en title/aria-label pour l'accessibilité. */}
      <button
        onClick={openOld}
        className="h-9 w-9 flex items-center justify-center btn-mini transition-colors"
        title="Ajouter un ancien diagnostic"
        aria-label="Ajouter un ancien diagnostic"
      >
        <IcPlus />
      </button>
    </div>
  );

  const extrasList = isDiagnostic && extras.length > 0 && (
    <div className="divide-y divide-gray-100 border-b border-gray-100">
      {extras.map((r) => (
        <div key={r.id} className="flex items-center gap-3 px-5 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900">{fmtDate(r.date)}</p>
            {r.note && <p className="text-xs text-gray-500 truncate">{r.note}</p>}
          </div>
          {r.link && (
            <a href={toUrl(r.link)} target="_blank" rel="noreferrer" className="btn-s gap-1.5 px-3 py-1.5 text-sm">
              <IcExternalLink className="text-xs" /> Ouvrir
            </a>
          )}
          <button onClick={() => deleteOld(r.id)} className="btn-db px-2.5 py-1.5 text-sm">Suppr.</button>
        </div>
      ))}
    </div>
  );

  const oldModal = showOld && (
    <div className="modal-backdrop flex items-center justify-center z-[60] p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowOld(false); }}>
      <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-md">
        {/* Composants-2 §10 : entête aplat encre + ✕ */}
        <div className="modal-head -mx-6 -mt-6 mb-4 rounded-t-2xl">
          <h3 className="modal-title text-lg">Ajouter un ancien diagnostic</h3>
          <button type="button" onClick={() => setShowOld(false)} className="text-gray-400 hover:text-white" aria-label="Fermer"><IcClose size={17} /></button>
        </div>
        <label className="block text-sm font-medium text-gray-600 mb-1">Date du diagnostic</label>
        <input type="date" value={oldForm.date} onChange={(e) => setOldForm((f) => ({ ...f, date: e.target.value }))} className="w-full mb-3 px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900" />
        <label className="block text-sm font-medium text-gray-600 mb-1">Lien / QR</label>
        <input type="text" value={oldForm.link} onChange={(e) => setOldForm((f) => ({ ...f, link: e.target.value }))} placeholder="https://... ou contenu du QR" className="w-full mb-3 px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900" />
        <label className="block text-sm font-medium text-gray-600 mb-1">Note (optionnel)</label>
        <textarea value={oldForm.note} onChange={(e) => setOldForm((f) => ({ ...f, note: e.target.value }))} rows={2} className="w-full mb-4 px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900" />
        <div className="flex gap-2">
          <button onClick={() => setShowOld(false)} className="btn-g flex-1 px-4 py-2.5">Annuler</button>
          <button onClick={saveOld} disabled={savingOld} className="btn-p flex-1 px-4 py-2.5">
            {savingOld && <BtnSk w={40} />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) return <div className="py-10 text-center text-gray-400"><Sk w={40} h={10} className="sk-inline mr-2" /> Chargement…</div>;
  if (error) return <div className="py-10 text-center text-red-500 text-sm">{error}</div>;

  if (shown.length === 0 && !(isDiagnostic)) {
    return (
      <div className="py-10 text-center text-gray-400 text-sm px-4">
        Aucune entrée « {label} ».<br />
        <span className="text-xs">Les « {label} » du client apparaissent ici après une vente.</span>
      </div>
    );
  }

  if (shown.length === 0 && isDiagnostic) {
    return (
      <div>
        {oldControls}
        {extrasList}
        {extras.length === 0 && (
          <div className="py-10 text-center text-gray-400 text-sm px-4">
            Aucun diagnostic visage.<br />
            <span className="text-xs">Après une vente, ou via « Ajouter un ancien diagnostic ».</span>
          </div>
        )}
        {oldModal}
      </div>
    );
  }

  if (compact) {
    return (
      <div>
        {oldControls}
        {extrasList}
        {shown.map((r) => {
          const hasLink = !!r.record?.link;
          const img = r.record?.image_url;
          const busy = uploadingId === r.sale_service_id;
          return (
            <div key={r.sale_service_id} className="px-4 py-2.5 border-t border-gray-100 flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{r.service_name}</p>
                <p className="text-[11px] text-gray-400">
                  {fmtDateShort(r.date)}
                  <span className={r.paid ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}> · {r.paid ? 'Payé' : 'Crédit'}</span>
                  <span className="tabular-nums"> · {fmtMoney(r.montant)}</span>
                </p>
              </div>
              {mode === 'photo' && (
                <>
                  {img && (
                    <a href={img} target="_blank" rel="noreferrer" className="shrink-0">
                      <img src={img} alt="" className="h-9 w-9 rounded-lg object-cover border border-gray-200" />
                    </a>
                  )}
                  <button
                    onClick={() => pickPhoto(r)}
                    disabled={busy}
                    aria-label={img ? 'Changer la photo' : 'Ajouter une photo'}
                    className={`shrink-0 h-9 w-9 rounded-lg flex items-center justify-center border ${
                      img ? 'border-gray-200 text-gray-600' : 'bg-green-600 border-green-600 text-white'
                    } ${busy ? 'opacity-60 cursor-wait' : ''}`}
                  >
                    {busy ? <BtnSk w={40} /> : <IcCamera className="text-xs" />}
                  </button>
                </>
              )}
              {mode === 'link' && (
                <button
                  onClick={() => handleLink(r)}
                  aria-label={hasLink ? 'Ouvrir le lien' : 'Ajouter un lien / QR'}
                  className={`shrink-0 h-9 w-9 rounded-lg flex items-center justify-center ${
                    hasLink ? 'btn-s' : 'border border-gray-200 text-gray-600'
                  }`}
                >
                  {hasLink ? <IcExternalLink className="text-xs" /> : <IcPlus className="text-xs" />}
                </button>
              )}
            </div>
          );
        })}
        {mode === 'photo' && (
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
        )}
        {selected && (
          <ServiceRecordDetailModal
            row={selected}
            customerId={customerId}
            category={category}
            onClose={() => setSelected(null)}
            onSaved={() => { setSelected(null); load(); }}
          />
        )}
        {oldModal}
      </div>
    );
  }

  return (
    <div>
      {oldControls}
      {extrasList}
      <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead className="bg-slate-50 sticky top-0">
          <tr>
            <th className="px-3 sm:px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
            <th className="px-3 sm:px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Service</th>
            <th className="px-3 sm:px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Montant</th>
            <th className="px-3 sm:px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Statut</th>
            {mode !== 'none' && (
              <th className="px-3 sm:px-5 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{mode === 'photo' ? 'Photo' : 'Lien'}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {shown.map((r) => {
            const hasLink = !!r.record?.link;
            const img = r.record?.image_url;
            const busy = uploadingId === r.sale_service_id;
            return (
              <tr key={r.sale_service_id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-3 sm:px-5 py-3 whitespace-nowrap text-sm text-gray-500">{fmtDate(r.date)}</td>
                <td className="px-3 sm:px-5 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{r.service_name}</td>
                <td className="px-3 sm:px-5 py-3 whitespace-nowrap text-sm text-gray-900">{fmtMoney(r.montant)}</td>
                <td className="px-3 sm:px-5 py-3 whitespace-nowrap">
                  {r.paid
                    ? <span className="bdg bdg-pos"><IcCheckCircle className="text-[10px]" /> Payé</span>
                    : <span className="bdg bdg-critical"><IcAlertCircle className="text-[10px]" /> Non payé</span>}
                </td>
                {mode !== 'none' && (
                <td className="px-3 sm:px-5 py-3 whitespace-nowrap text-right">
                  {mode === 'photo' ? (
                    <div className="flex items-center justify-end gap-2">
                      {img && (
                        <a href={img} target="_blank" rel="noreferrer">
                          <img src={img} alt="" className="h-11 w-11 rounded-lg object-cover border border-gray-200" />
                        </a>
                      )}
                      <button
                        onClick={() => pickPhoto(r)}
                        disabled={busy}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border ${
                          img ? 'border-gray-200 text-gray-600 hover:bg-gray-100' : 'bg-green-600 border-green-600 text-white hover:bg-green-700'
                        } ${busy ? 'opacity-60 cursor-wait' : ''}`}
                      >
                        {busy ? <BtnSk w={40} /> : <IcCamera className="text-xs" />}
                        {img ? 'Changer' : 'Ajouter photo'}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleLink(r)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${
                        hasLink ? 'btn-s' : 'border border-gray-200 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {hasLink ? <><IcExternalLink className="text-xs" /> Ouvrir</> : <><IcPlus className="text-xs" /> Ajouter lien / QR</>}
                    </button>
                  )}
                </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      {/* Hidden file input shared by the photo rows */}
      {mode === 'photo' && (
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
      )}

      {selected && (
        <ServiceRecordDetailModal
          row={selected}
          customerId={customerId}
          category={category}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); load(); }}
        />
      )}
      {oldModal}
    </div>
  );
};

export default ClientRecordsPanel;
